import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";

const date = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const Info = ({ label, value, testid }) => (
  <div>
    <p className="text-[11px] uppercase tracking-widest text-mist">{label}</p>
    <p className="break-all font-mono-ui text-sm text-fg2" data-testid={testid}>{value || "—"}</p>
  </div>
);

export const CustomersManager = ({ onChange }) => {
  const [rows, setRows] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    http.get("/admin/customers").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await http.delete(`/admin/customers/${removing.id}`);
      toast.success("Cliente eliminato");
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setRemoving(null);
  };

  if (rows === null) return null;
  if (!rows.length) return <p className="py-16 text-center text-mist" data-testid="admin-customers-empty">Nessun cliente registrato.</p>;

  return (
    <div className="mt-4 grid gap-3" data-testid="admin-customers-list">
      <p className="text-sm text-mist" data-testid="admin-customers-count">{rows.length} {rows.length === 1 ? "cliente registrato" : "clienti registrati"} · numerati in ordine di registrazione</p>
      {rows.map((c, i) => (
        <article key={c.id} data-testid={`admin-customer-${c.id}`} className="grid gap-4 rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-5 md:grid-cols-12 md:items-center">
          <div className="flex gap-4 md:col-span-3">
            <span className="flex h-10 min-w-10 shrink-0 items-center justify-center rounded-full bg-[var(--paper)] px-2 font-display text-sm font-semibold text-[var(--ink)]" data-testid={`admin-customer-number-${c.id}`}>{rows.length - i}</span>
            <div className="min-w-0">
            <p className="font-display text-lg">{c.name}</p>
            <p className="break-all text-sm text-mist">{c.email}</p>
            {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="block text-sm text-fg2 hover:underline" data-testid={`admin-customer-phone-${c.id}`}>{c.phone}</a>}
            <p className="mt-1 text-xs text-mist">{c.paid_orders} ordini pagati</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:col-span-8 md:grid-cols-4">
            <Info label="Registrato il" value={date(c.created_at)} testid={`admin-customer-created-${c.id}`} />
            <Info label="IP registrazione" value={c.registration_ip} testid={`admin-customer-reg-ip-${c.id}`} />
            <Info label="Ultimo accesso" value={date(c.last_login_at)} />
            <Info label="IP ultimo accesso" value={c.last_login_ip} testid={`admin-customer-last-ip-${c.id}`} />
          </div>
          <button onClick={() => setRemoving(c)} data-testid={`admin-customer-delete-${c.id}`} aria-label="Elimina cliente"
            className="flex h-10 w-10 items-center justify-center justify-self-end rounded-full text-[var(--flare)] hover:bg-fg/10 md:col-span-1">
            <Trash2 className="h-4 w-4" />
          </button>
        </article>
      ))}
      <ConfirmDialog open={!!removing} testid="admin-customer-delete-dialog" onCancel={() => setRemoving(null)} onConfirm={remove}
        title={`Eliminare ${removing?.name}?`}
        description="L'account verrà cancellato e il cliente non potrà più accedere. Gli ordini già pagati restano nello storico." />
    </div>
  );
};
